import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, Globe, Mail, Phone, MapPin, ArrowLeft } from "lucide-react";
import axios from "axios";

const PartnerDetail = () => {
  const { id } = useParams();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    const fetchPartner = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/partners/${id}`);
        setPartner(res.data);
      } catch (error) {
        console.error("Failed to fetch partner", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPartner();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-bold">Partner Not Found</h1>
        <Button asChild variant="outline">
          <Link to="/partners">Back to Directory</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-20">
      {/* Header / Breadcrumb Area */}
      <div className="bg-white border-b py-8">
        <div className="container mx-auto px-4">
          <Link to="/partners" className="inline-flex items-center text-sm text-gray-500 hover:text-primary mb-4">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Partners
          </Link>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8">
            <div className="h-32 w-32 bg-white border rounded-lg p-4 flex items-center justify-center shadow-sm">
               {partner.logo_url ? (
                  <img 
                    src={partner.logo_url.startsWith('http') ? partner.logo_url : `${process.env.REACT_APP_BACKEND_URL}${partner.logo_url}`} 
                    alt={partner.name} 
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-2xl font-bold text-gray-300">{partner.name[0]}</span>
                )}
            </div>
            <div>
              <h1 className="text-4xl font-heading font-bold uppercase mb-2">{partner.name}</h1>
              <div className="flex flex-wrap gap-2">
                {partner.industries.map(ind => (
                  <span key={ind} className="bg-zinc-100 text-zinc-700 text-xs px-2 py-1 rounded font-medium uppercase tracking-wide">
                    {ind}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left: Main Content */}
        <div className="lg:col-span-2 space-y-8">
          <div className="bg-white p-8 rounded-lg shadow-sm border">
            <h2 className="text-xl font-heading font-bold uppercase mb-4 border-b pb-2">About {partner.name}</h2>
            <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
              {partner.description || "No description available."}
            </p>
          </div>

          {(partner.services && partner.services.length > 0) && (
            <div className="bg-white p-8 rounded-lg shadow-sm border">
              <h2 className="text-xl font-heading font-bold uppercase mb-4 border-b pb-2">Services</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {partner.services.map(s => (
                  <li key={s} className="flex items-center text-gray-600">
                    <div className="h-2 w-2 bg-primary rounded-full mr-3" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Right: Sidebar Info */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="font-bold uppercase tracking-wide mb-4 text-sm text-gray-400">Contact Information</h3>
            <div className="space-y-4">
              {partner.website && (
                <div className="flex items-start gap-3">
                  <Globe className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <span className="block text-xs text-gray-400 uppercase">Website</span>
                    <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">
                      {partner.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                </div>
              )}
              
              {partner.contact_email && (
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <span className="block text-xs text-gray-400 uppercase">Email</span>
                    <a href={`mailto:${partner.contact_email}`} className="text-gray-700 hover:text-primary break-all">
                      {partner.contact_email}
                    </a>
                  </div>
                </div>
              )}

              {partner.phone && (
                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <span className="block text-xs text-gray-400 uppercase">Phone</span>
                    <a href={`tel:${partner.phone}`} className="text-gray-700 hover:text-primary">
                      {partner.phone}
                    </a>
                  </div>
                </div>
              )}

              {partner.regions && partner.regions.length > 0 && (
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <span className="block text-xs text-gray-400 uppercase">Regions Served</span>
                    <span className="text-gray-700">{partner.regions.join(", ")}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerDetail;
